import React, { useCallback, useMemo } from 'react';

import classnames from 'classnames';

import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import LinkButton from 'components/uiLibrary/LinkButton';
import SeparatorList from 'components/uiLibrary/SeparatorList';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';
import VideoCard from 'components/Media/Display/VideoCard'; // eslint-disable-line import/no-cycle
import { usePaywallContext } from 'components/Paywall';
import { SECTION_KEYS } from 'components/Productions/Display/QuickView/constants';
import RentalBanner from 'components/Productions/Display/RentalBanner';

import { TP, MEDIA_ATTRIBUTES_TYPES_IDS } from 'constants/index';

import { getProgramLabel } from 'utils/common';
import ImpressionTracker from 'utils/components/impressionTracker';
import useNavigate from 'utils/hooks/useNavigate';
import { getMediaSource } from 'utils/media';
import { convertTimeFormat, getProductionTitles, getCastCrewItems, getInsEnsemblesItems } from 'utils/productions';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Media/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import Actions from '../Actions';
import Entirety from '../Entirety';
import EntityRoles from '../EntityRoles';
import Poster from '../MediaList/Poster';
import PerformanceDatesInfo from '../PerformanceDatesInfo';
import ProductionOtherTags from '../ProductionOtherTags';
import ProductionTags from '../ProductionTags';
import PublicVenuesAndDates from '../PublicVenuesAndDates';

import classes from './ProductionPublicSlugDesktop.module.scss';

const ComposersList = React.memo(({ composers, trackingData }) => (
  <SeparatorList
    data={composers?.map(props => (
      <EntityName key={props?.entity?.id} {...props} trackingData={trackingData} />
    ))}
  />
));

const TitleWrapper = ({ children, isCancelled }) => (
  <Typography variant="p" strikethrough={isCancelled}>
    {children}
  </Typography>
);

const WorkTitles = ({ titles, trackingData, slugSeoH4, isArchived, isCancelled, production }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { setIsPaywallExpanded } = usePaywallContext();

  if (titles?.length === 1) {
    return null;
  }

  const hasCustomTitle = titles?.[0]?.isCustomTitle;
  const slicedTitles = hasCustomTitle ? titles.slice(1) : titles;

  return (
    <div className={classes.workTitles}>
      {slicedTitles?.map(
        ({ title, translation, composers, entityType, entityId, workType, workExcerptReference }, index) => (
          <div className={classes.workTitles__item} key={`${title}-${index}`}>
            <div className={classes.visuallyHidden}>
              <h4 id="performance-details" aria-hidden="true">
                {slugSeoH4}
              </h4>
            </div>
            <div className={classes.workTitles__item_left}>
              <Typography
                className={classes.workType}
                size={10}
                truncate
                color="secondary"
                blur={isArchived}
                {...(isArchived && {
                  onClick: e => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsPaywallExpanded(true);
                  },
                })}
              >
                {workType}
              </Typography>
            </div>
            <div className={classes.workTitles__item_right}>
              <TitleWrapper isCancelled={isCancelled}>
                <Typography weight="medium" size={12} className={classes.workTitle} color="secondary">
                  <EntityName
                    name={title}
                    entityType={entityType}
                    entity={{ id: entityId }}
                    trackingData={trackingData}
                    isRaw
                  />
                </Typography>
                {translation && (
                  <Typography size={12} weight="medium" color="secondary">
                    {' '}
                    ({translation})
                  </Typography>
                )}
                <Typography size={12} italic color="secondary">
                  , <ComposersList composers={composers} trackingData={trackingData} />
                </Typography>
                {workExcerptReference && (
                  <Typography className={classes.workExcerptReference} variant="p" size={12} italic color="secondary">
                    {workExcerptReference}
                  </Typography>
                )}
              </TitleWrapper>
            </div>
          </div>
        ),
      )}
      {production?.isProgramNotAnnounced && (
        <div className={classes.workTitles__item}>
          <div className={classes.workTitles__item_left} />
          <div className={classes.workTitles__item_right}>
            <Typography className={classes.programNotAnnounced} variant="p" size={12} italic color="secondary" truncate>
              *{t(getProgramLabel(production, production?.productionWorks?.length))}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductionPublicSlugDesktop = ({
  data,
  producerNames,
  contributors,
  linkProps,
  handlePerformanceClick,
  trackingData,
  entity,
  entityType,
  className,
  upcoming,
  slugSeoH4,
  setSectionKey,
  showLightbox,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const { setIsPaywallExpanded } = usePaywallContext();
  const isCancelled = data?.isCancelled;
  const isArchived = data?.isArchived;
  const titles = useMemo(() => getProductionTitles(data, true), [data]);
  const productionCustomTitle = titles?.[0] ? titles[0].title : null;
  const aboutData = data?.description?.length > 0 && data?.description;

  const availableLanguages = data?.languages?.map(language => language?.name).join(', ');
  const availableSurtitles = data?.surtitles?.map(language => language?.name).join(', ');
  const timeDuration = useMemo(() => convertTimeFormat(data?.duration), [data?.duration]);

  const stats = data?.stats || {};

  const { directors, conductors, entityRoles } = contributors;

  const castCrewItems = useMemo(() => getCastCrewItems(contributors, entity), [contributors, entity]);
  const insEnsemblesItems = useMemo(() => getInsEnsemblesItems(contributors, navigate, entity), [
    contributors,
    navigate,
    entity,
  ]);

  const handleSectionKey = (e, key) => {
    e.preventDefault();
    setSectionKey(key);
  };

  const castCrewLinkProps = useCallback(
    ({ castItem, castItemEntityType }) =>
      navigate.getLinkProps({
        entityType: castItemEntityType,
        entity: castItem,
      }),
    [navigate],
  );

  const { hasPhotos, hasVideos } = useMemo(
    () => ({
      hasPhotos: data?.imageCount > 1 || data?.stats?.media?.images?.total > 1,
      hasVideos: data?.videoCount > 1 || data?.stats?.media?.videos?.total > 1,
    }),
    [data?.imageCount, data?.stats?.media?.images?.total, data?.videoCount, data?.stats?.media?.videos?.total],
  );

  const { data: videosData } = useQuery(
    queries.getFeaturedVideos({
      entityId: data.id,
      entityType: ENTITY_TYPES.PRODUCTION,
      queryConfig: { enabled: hasVideos },
    }),
  );

  const { data: photosData } = useQuery(
    queries.getFeaturedPhotos({
      entityId: data?.id,
      entityType: ENTITY_TYPES.PRODUCTION,
      filters: {
        exclude_media_attribute_id: MEDIA_ATTRIBUTES_TYPES_IDS.POSTER,
      },
      queryConfig: { enabled: hasPhotos && !hasVideos },
    }),
  );

  const { data: photos } = photosData || {};
  const { data: videos } = videosData || {};

  const photosTrackingData = {
    ...trackingData,
    component: COMPONENTS.PHOTOS,
  };

  const openLightbox = mediaId => {
    showLightbox({ data: photos, mediaId, trackingData: photosTrackingData });
  };

  return (
    <ImpressionTracker
      as="li"
      data={{
        ...trackingData,
        entityId: data.id,
        entityName: data.name,
        entityType: ENTITY_TYPES.PRODUCTION,
      }}
      className={classnames(classes.slugItem, 'h-event')}
    >
      <article className={classnames(classes.root, className)} onClick={handlePerformanceClick}>
        <div>
          {data?.rentals?.length > 0 && (
            <div className={classes.rentalBannerWrapper}>
              <RentalBanner
                entity={data}
                styles={{
                  rentalBanner: classes.rentalBanner,
                }}
                isFullBanner
              />
            </div>
          )}
          <div className={classes.ppsDesktop}>
            <div className={classes.ppsDesktop__left}>
              <div className={classes.ppsDesktop__left_top}>
                <div className={classes.entiretyMainTag}>
                  <div className={classes.entirety}>
                    <Entirety production={data} />
                  </div>
                  <div className={classes.productionMainTags}>
                    <ProductionTags data={data} />
                  </div>
                </div>
                <div className={classes.otherTagsTitle}>
                  <div className={classes.otherTagsTitle__top}>
                    <ProductionOtherTags production={data} />
                    {timeDuration && (
                      <Typography
                        size={12}
                        className={classes.duration}
                        color="secondary"
                        {...(isCancelled && { strikethrough: true })}
                      >
                        {t(`${TP}.FN_PRODUCTION_DURATION_TITLE`)}: {timeDuration}
                        {(availableLanguages?.length > 0 || availableSurtitles?.length > 0) && (
                          <span className={classes.separator}>{' | '}</span>
                        )}
                      </Typography>
                    )}
                    {availableLanguages?.length > 0 && (
                      <Typography
                        size={12}
                        color="secondary"
                        {...(isCancelled && { strikethrough: true })}
                        className={classes.language}
                      >
                        {t(`${TP}.FN_LANGUAGE_LABEL`)}: {availableLanguages}
                        {availableSurtitles?.length > 0 && <span className={classes.separator}>{' | '}</span>}
                      </Typography>
                    )}
                    {availableSurtitles?.length > 0 && (
                      <Typography
                        size={12}
                        className={classes.surtitle}
                        color="secondary"
                        {...(isCancelled && { strikethrough: true })}
                        truncate
                      >
                        {t(`${TP}.SURTITLE`)}: {availableSurtitles}
                      </Typography>
                    )}
                  </div>
                  <div className={classes.otherTagsTitle__bottom}>
                    <div className={classes.mainTitle}>
                      {productionCustomTitle && (
                        <>
                          <Typography weight="bold" className={classes.customTitle} truncate>
                            {productionCustomTitle}
                            {titles?.[0]?.composers?.length > 0 && ','}
                          </Typography>
                          <Typography italic className={classes.composersCustomTitle}>
                            <ComposersList composers={titles?.[0]?.composers} trackingData={trackingData} />
                          </Typography>
                        </>
                      )}
                    </div>
                    <div className={classes.producer}>{producerNames}</div>
                    <div className={classes.directorConductor}>
                      <Typography className={classes.directorConductor} size={12} color="secondary">
                        {conductors?.data?.length > 0 && (
                          <>
                            C:
                            {conductors.data.map(({ entity: conductor }, idx) => (
                              <LinkButton
                                key={conductor.id || idx}
                                variant="text"
                                {...navigate.getLinkProps({ entityType: ENTITY_TYPES.PROFILE, entity: conductor })}
                                preventDefault
                                isLink
                                trackingData={{
                                  ...trackingData,
                                  component: COMPONENTS.CONDUCTOR_DIRECTOR,
                                }}
                              >
                                <EntityName
                                  entityType={ENTITY_TYPES.PROFILE}
                                  entity={conductor}
                                  isRaw
                                  trackingData={trackingData}
                                />
                                {idx < conductors.data.length - 1 ? ', ' : ''}
                              </LinkButton>
                            ))}
                          </>
                        )}
                        {directors?.data?.length > 0 && (
                          <>
                            <span className={classes.directorLabel}>D:</span>
                            {directors.data.map(({ entity: director }, idx) => (
                              <LinkButton
                                key={director.id || idx}
                                variant="text"
                                {...navigate.getLinkProps({ entityType: ENTITY_TYPES.PROFILE, entity: director })}
                                preventDefault
                                isLink
                                trackingData={{
                                  ...trackingData,
                                  component: COMPONENTS.CONDUCTOR_DIRECTOR,
                                }}
                              >
                                <EntityName
                                  entityType={ENTITY_TYPES.PROFILE}
                                  entity={director}
                                  isRaw
                                  trackingData={trackingData}
                                />
                                {idx < directors.data.length - 1 ? ', ' : ''}
                              </LinkButton>
                            ))}
                          </>
                        )}
                      </Typography>
                    </div>
                  </div>
                </div>
                <div className={classes.tickets}>
                  <Actions data={data} entity={entity} entityType={entityType} trackingData={trackingData} />
                </div>
              </div>
              <div className={classes.ppsDesktop__left_bottom}>
                <div className={classes.dates}>
                  {upcoming ? (
                    <div className={classes.upcomingDates}>
                      <PerformanceDatesInfo
                        data={data}
                        linkProps={linkProps}
                        onClick={handlePerformanceClick}
                        styles={{
                          root: classes.performanceDatesInfo,
                          dateTag: classes.performanceDatesInfoDateTag,
                          cityCountry: classes.cityCountry,
                          timeTagTime: classes.timeTagTime,
                          timeTagDay: classes.timeTagDay,
                          allDates: classes.allDates,
                          desktopVenueInfoText: classes.desktopVenueInfoText,
                          locationIcon: classes.locationIcon,
                          groupedDateList: classes.groupedDateList,
                        }}
                        trackingData={trackingData}
                      />
                    </div>
                  ) : (
                    <div className={classes.seasonDatesVenues}>
                      <PublicVenuesAndDates production={data} linkProps={linkProps} trackingData={trackingData} />
                    </div>
                  )}
                </div>
                <div className={classes.worksAbout}>
                  <WorkTitles
                    titles={titles}
                    trackingData={trackingData}
                    slugSeoH4={slugSeoH4}
                    isArchived={isArchived}
                    isCancelled={isCancelled}
                    production={data}
                  />
                  {aboutData && (
                    <Typography
                      size={12}
                      color="secondary"
                      className={classnames(classes.about, { [classes.aboutSingle]: titles?.length > 1 })}
                      italic
                    >
                      <Typography size={12} color="secondary" weight="medium" italic>
                        {t(`${TP}.m_ABOUT`)} {': '}
                      </Typography>
                      {aboutData}
                      <Typography size={12} color="secondary" className={classes.anchor} italic>
                        <Typography size={12} color="secondary" italic className={classes.readMore}>
                          {t(`${TP}.FN_READ_MORE`)}
                        </Typography>
                      </Typography>
                    </Typography>
                  )}
                </div>
                <div className={classes.castCrew}>
                  {entityRoles?.length > 0 && (
                    <div className={classes.castCrew__entityRoles}>
                      <EntityRoles
                        data={data}
                        entity={entity}
                        entityType={entityType}
                        entityRoles={entityRoles}
                        trackingData={trackingData}
                      />
                    </div>
                  )}
                  <div className={classes.castCrewList}>
                    {castCrewItems?.map(item => (
                      <div className={classes.castCrewList__items} key={`${item?.id}-${item?.entity?.id}`}>
                        <Typography
                          size={12}
                          truncate
                          italic
                          color="secondary"
                          className={classes.castCrewList__items_credit}
                        >
                          {item?.credit}
                        </Typography>
                        <Typography
                          size={12}
                          truncate
                          color="secondary"
                          className={classes.castCrewList__items_name}
                          blur={isArchived}
                          {...(isArchived && {
                            onClick: e => {
                              e.stopPropagation();
                              e.preventDefault();
                              setIsPaywallExpanded(true);
                            },
                          })}
                        >
                          {' '}
                          <LinkButton
                            variant="text"
                            preventDefault
                            isLink
                            {...castCrewLinkProps({
                              castItem: item?.entity,
                              castItemEntityType: ENTITY_TYPES.PROFILE,
                            })}
                            trackingData={{
                              ...trackingData,
                              component: COMPONENTS.CAST_CREW,
                            }}
                          >
                            <EntityName
                              entityType={ENTITY_TYPES.PROFILE}
                              entity={item?.entity}
                              isRaw
                              trackingData={trackingData}
                            />
                          </LinkButton>
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={classes.insEnsembles}>
                  <div className={classes.insEnsembles__items}>
                    {insEnsemblesItems?.map(item => (
                      <div className={classes.insEnsembles__item} key={item?.id}>
                        {item?.ensembleLinkProps ? (
                          <Typography variant="h6" size={12} className={classes.ensembleItem} truncate>
                            <LinkButton
                              variant="text"
                              {...item?.ensembleLinkProps}
                              preventDefault
                              isLink
                              trackingData={trackingData}
                            >
                              <EntityName
                                entityType={ENTITY_TYPES.ORGANIZATION}
                                entity={item}
                                isRaw
                                trackingData={trackingData}
                              />
                            </LinkButton>
                          </Typography>
                        ) : (
                          <div className={classes.instrumentalistItem}>
                            <Typography
                              size={12}
                              truncate
                              italic
                              color="secondary"
                              className={classes.instrumentalistItem__credit}
                            >
                              {item?.credit}
                            </Typography>
                            <Typography
                              size={12}
                              truncate
                              color="secondary"
                              className={classes.instrumentalistItem__name}
                              blur={isArchived}
                              {...(isArchived && {
                                onClick: e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setIsPaywallExpanded(true);
                                },
                              })}
                            >
                              <LinkButton
                                variant="text"
                                preventDefault
                                isLink
                                {...castCrewLinkProps({
                                  castItem: item?.entity,
                                  castItemEntityType: ENTITY_TYPES.PROFILE,
                                })}
                              >
                                {' '}
                                <EntityName
                                  entityType={ENTITY_TYPES.PROFILE}
                                  entity={item?.entity}
                                  isRaw
                                  trackingData={trackingData}
                                />
                              </LinkButton>
                            </Typography>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <Typography size={11} color="secondary" truncate className={classes.viewAllcontributorsAction}>
                    {t(`${TP}.VIEW_CAST_CREW_INS_ENSEMBLES`)}
                  </Typography>
                </div>
              </div>
            </div>
            <div className={classes.ppsDesktop__right}>
              <div className={classes.ppsDesktop__right_top}>
                <div className={classes.poster}>
                  <Poster production={data} />
                </div>
                <div className={classes.video}>
                  {data?.trailer?.url || videos?.[0]?.url ? (
                    <VideoCard
                      data={data?.trailer || videos?.[0]}
                      hideVideoInfoForce
                      styles={{ root: classes.videoCard }}
                      width={172}
                      height={100}
                    />
                  ) : (
                    <Image
                      src={getMediaSource(photos?.[0], true)}
                      blurHash={photos?.[0]?.blurHash || photos?.[0]?.file?.urls?.blurHash}
                      width={172}
                      height={100}
                      transformations={TRANSFORMATIONS.SLUG_MEDIA}
                      onClick={() => openLightbox(photos?.[0]?.id)}
                      trackingData={photosTrackingData}
                      disableNextImage
                      useIntersectionObserver
                      lazy
                    />
                  )}
                </div>
              </div>
              <div className={classes.ppsDesktop__right_bottom}>
                <div className={classes.mediaStatsLeft}>
                  {(stats?.reviews?.exists || data?.reviewCount > 0) && (
                    <span className={classes.actionText} onClick={e => handleSectionKey(e, SECTION_KEYS.REVIEWS)}>
                      <SpriteIcon icon="reviews" size={12} />
                      <Typography color="secondary" size={12}>
                        {t(`${TP}.m_REVIEWS`)}
                      </Typography>
                    </span>
                  )}
                </div>
                <div className={classes.mediaStatsRight}>
                  {(stats?.media?.videos?.exists || data?.videoCount > 0) && (
                    <span className={classes.actionText} onClick={e => handleSectionKey(e, SECTION_KEYS.VIDEOS)}>
                      <SpriteIcon icon="image" size={12} />
                      <Typography color="secondary" size={12}>
                        {t(`${TP}.m_VIDEOS`)}
                      </Typography>
                    </span>
                  )}
                  {(stats?.media?.images?.exists || data?.imageCount > 0) && (
                    <span className={classes.actionText} onClick={e => handleSectionKey(e, SECTION_KEYS.PHOTOS)}>
                      <SpriteIcon icon="image" size={12} />
                      <Typography color="secondary" size={12}>
                        {t(`${TP}.m_IMAGES`)}
                      </Typography>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </ImpressionTracker>
  );
};

export default ProductionPublicSlugDesktop;
